import React, { useCallback, useEffect, useMemo, useState } from "react";
import config from "../config.js";
import pinPrompt from "../utils/pinPrompt";
import { getFileTypeBadge, COMPONENT_DOC_TYPES, docTypeLabel } from "../utils/fileBadges";
import { apiErrorMessage, detailToString } from '../utils/apiError';

/**
 * Files on a PURCHASED COMPONENT — the drawing the vendor builds to, the STEP
 * model, the material cert, the photo.
 *
 * Two things the parts Files card does not have:
 *   - a doc_type, so a purchase print is distinguishable from a photo;
 *   - a `vendor_visible` flag, which is what decides whether the file rides
 *     along on the PO email. It defaults off: nothing leaves the building
 *     because someone forgot to think about it.
 *
 * A shadow-twin (a component whose spec is owned by an in-house part) renders
 * the PART's documents, read-only, with a link through. One physical item, one
 * set of drawings.
 */
export default function ComponentFilesCard({ consumableId, productId, onChanged }) {
  const API_BASE = config.API_BASE_URL;

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRetired, setShowRetired] = useState(false);

  const [file, setFile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [docType, setDocType] = useState("drawing");
  const [vendorVisible, setVendorVisible] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = useCallback(async () => {
    if (!consumableId) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE}/components/${consumableId}/documents?include_retired=${showRetired}`
      );
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setPayload(await res.json());
    } catch (err) {
      setError(`Could not load files: ${err.message}`);
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, consumableId, showRetired]);

  useEffect(() => {
    load();
  }, [load]);

  const readOnly = payload?.read_only === true;
  const documents = useMemo(() => payload?.documents || [], [payload]);

  async function handleUpload(e) {
    e.preventDefault();
    if (!file) {
      setError("Choose a file first.");
      return;
    }
    const pin = await pinPrompt("PIN to attach this file:");
    if (!pin) return;

    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("pin", pin);
      form.append("display_name", displayName.trim() || file.name);
      if (description.trim()) form.append("description", description.trim());
      form.append("doc_type", docType);
      form.append("vendor_visible", vendorVisible ? "true" : "false");
      form.append("file", file);

      const res = await fetch(`${API_BASE}/components/${consumableId}/documents`, {
        method: "POST",
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(data.detail) || "Upload failed.");

      setFile(null);
      setDisplayName("");
      setDescription("");
      const input = document.getElementById(`cfc-file-${consumableId}`);
      if (input) input.value = "";
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function patchDoc(documentId, body, promptText) {
    const pin = await pinPrompt(promptText);
    if (!pin) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/components/${consumableId}/documents/${documentId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, ...body }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(data.detail) || "Update failed.");
      setEditingId(null);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleRetire(doc) {
    if (
      !window.confirm(
        `Retire "${doc.display_name}"?\n\nIt stops showing on the current revision but stays ` +
          `readable on the revisions it applied to. The file is not deleted.`
      )
    )
      return;
    const pin = await pinPrompt("PIN to retire this file:");
    if (!pin) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE}/components/${consumableId}/documents/${doc.component_document_id}/retire`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pin }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(data.detail) || "Retire failed.");
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(doc) {
    if (!window.confirm(`Permanently delete "${doc.display_name}"? This cannot be undone.`)) return;
    const pin = await pinPrompt("PIN to delete this file:");
    if (!pin) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE}/components/${consumableId}/documents/${doc.component_document_id}` +
          `?pin=${encodeURIComponent(pin)}`,
        { method: "DELETE" }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(data.detail) || "Delete failed.");
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  function openDoc(doc) {
    // download_url is a short-lived presigned S3 GET. The global fetch wrapper
    // only attaches X-API-Key to our own API host, so opening it directly is
    // correct — no key travels to S3.
    if (doc.download_url) window.open(doc.download_url, "_blank", "noopener");
  }

  return (
    <div className="card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>📁 Files</strong>
        <div className="d-flex align-items-center gap-2">
          {readOnly && <span className="badge bg-info text-dark">from the part</span>}
          {!readOnly && (
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id={`cfc-retired-${consumableId}`}
                checked={showRetired}
                onChange={(e) => setShowRetired(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor={`cfc-retired-${consumableId}`}>
                show retired
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="card-body">
        {error && <div className="alert alert-danger py-2">{error}</div>}

        {readOnly && (
          <div className="alert alert-info py-2 small mb-3">
            {productId} is a stock pool for in-house part{" "}
            <a href={`/parts?part_id=${payload.governing_part_id}`}>
              {payload.governing_product_id} Rev {payload.governing_revision_code}
            </a>
            . Its drawings live on the part — add or change them there.
          </div>
        )}

        {loading && <div className="text-muted small">Loading…</div>}

        {!loading && documents.length === 0 && (
          <div className="text-muted small mb-3">
            No files yet. The drawing, the STEP model and the vendor's print belong here.
          </div>
        )}

        {documents.length > 0 && (
          <div className="table-responsive mb-3">
            <table className="table table-sm align-middle mb-0">
              <tbody>
                {documents.map((doc) => {
                  const badge = getFileTypeBadge(doc.original_filename);
                  const key = doc.component_document_id || `part-${doc.part_document_id}`;
                  const isEditing = editingId === doc.component_document_id;
                  return (
                    <tr key={key} className={doc.is_retired ? "table-secondary" : undefined}>
                      <td style={{ width: 60 }}>
                        <span className={`badge ${badge.color}`}>{badge.label}</span>
                      </td>
                      <td>
                        {isEditing ? (
                          <div className="input-group input-group-sm">
                            <input
                              className="form-control"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  patchDoc(
                                    doc.component_document_id,
                                    { display_name: editName },
                                    "PIN to rename this file:"
                                  );
                              }}
                              autoFocus
                            />
                            <button
                              className="btn btn-success"
                              disabled={busy}
                              onClick={() =>
                                patchDoc(
                                  doc.component_document_id,
                                  { display_name: editName },
                                  "PIN to rename this file:"
                                )
                              }
                            >
                              ✓
                            </button>
                            <button className="btn btn-outline-secondary" onClick={() => setEditingId(null)}>
                              ✕
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              className="btn btn-link p-0 text-start"
                              onClick={() => openDoc(doc)}
                              title={doc.original_filename}
                            >
                              {doc.display_name}
                            </button>
                            {doc.is_retired && (
                              <span className="badge bg-secondary ms-2">retired</span>
                            )}
                            {doc.vendor_visible && (
                              <span className="badge bg-warning text-dark ms-2" title="Attached to the PO email when we buy this">
                                → vendor
                              </span>
                            )}
                            <div className="small text-muted">
                              {docTypeLabel(doc.doc_type)}
                              {doc.uploaded_by_name ? ` · ${doc.uploaded_by_name}` : ""}
                              {doc.created_at
                                ? ` · ${new Date(doc.created_at).toLocaleDateString()}`
                                : ""}
                              {doc.description ? ` · ${doc.description}` : ""}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                        {!readOnly && !doc.is_retired && !isEditing && (
                          <>
                            <button
                              className="btn btn-sm btn-outline-secondary me-1"
                              title="Rename"
                              disabled={busy}
                              onClick={() => {
                                setEditingId(doc.component_document_id);
                                setEditName(doc.display_name);
                              }}
                            >
                              ✏️
                            </button>
                            <button
                              className={`btn btn-sm me-1 ${
                                doc.vendor_visible ? "btn-warning" : "btn-outline-warning"
                              }`}
                              title={
                                doc.vendor_visible
                                  ? "Goes to the vendor on POs — click to stop sending it"
                                  : "Not sent to the vendor — click to attach it to POs"
                              }
                              disabled={busy}
                              onClick={() =>
                                patchDoc(
                                  doc.component_document_id,
                                  { vendor_visible: !doc.vendor_visible },
                                  doc.vendor_visible
                                    ? "PIN to stop sending this to the vendor:"
                                    : "PIN to send this to the vendor on POs:"
                                )
                              }
                            >
                              →
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary me-1"
                              title="Retire — keeps the file, stops showing it on the current revision"
                              disabled={busy}
                              onClick={() => handleRetire(doc)}
                            >
                              🗄
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Delete permanently"
                              disabled={busy}
                              onClick={() => handleDelete(doc)}
                            >
                              🗑
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!readOnly && (
          <form onSubmit={handleUpload} className="border-top pt-3">
            <div className="row g-2 align-items-end">
              <div className="col-md-5">
                <label className="form-label small mb-1">File</label>
                <input
                  id={`cfc-file-${consumableId}`}
                  type="file"
                  className="form-control form-control-sm"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setFile(f);
                    if (f && !displayName.trim()) setDisplayName(f.name.replace(/\.[^.]+$/, ""));
                  }}
                />
                <div className="form-text">
                  PDF, DXF, DWG, STEP, IGES, PNG, JPG. Up to 25 MB.
                </div>
              </div>
              <div className="col-md-4">
                <label className="form-label small mb-1">Name</label>
                <input
                  className="form-control form-control-sm"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Hole Bar print"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small mb-1">Type</label>
                <select
                  className="form-select form-select-sm"
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                >
                  {COMPONENT_DOC_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-9">
                <input
                  className="form-control form-control-sm"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional note — e.g. 'J.Doff-Sotta 7/6/26, supersedes the hand sketch'"
                />
              </div>
              <div className="col-md-3 d-flex align-items-center justify-content-between">
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`cfc-vv-${consumableId}`}
                    checked={vendorVisible}
                    onChange={(e) => setVendorVisible(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor={`cfc-vv-${consumableId}`}>
                    send to vendor
                  </label>
                </div>
                <button className="btn btn-sm btn-primary" disabled={busy || !file}>
                  {busy ? "…" : "Attach"}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
